import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { DataService } from '@shared/services/data/data.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, map, shareReplay, takeUntil } from 'rxjs/operators';
import { ConfigService } from 'app/core/config/config.service';
import AppVersion from '../../../../assets/app-version.json';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { RoleContextService, PortalRole } from '@shared/services/role-context.service';
import { Drawer } from 'primeng/drawer';
import { Button } from 'primeng/button';
import { ReactiveFormsModule } from '@angular/forms';
import { SESSION_STORAGE_KEYS } from 'app/core/session_storage/constants/session-storage-keys.constant';
import { DepartmentCacheService } from '@shared/services/department-cache.service';
import { type DepartmentDTO } from '@shared/models/departmentDTO';
import { SideNavComponent } from './side-nav/side-nav.component';
import { getShellNavConfigForRole } from './navigation/shell-nav.config';
import { type ResolvedShellNavItem, type ShellNavContext, type ShellNavItemConfig } from './navigation/shell-nav.types';

const SHELL_MOBILE_BREAKPOINT = '(max-width: 1024px)';

@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  imports: [Drawer, Button, ReactiveFormsModule, SideNavComponent, RouterOutlet],
})
export class ShellComponent implements OnInit, OnDestroy {
  tenantName = '';

  portalHeader = 'Token Control';
  pageHeaderTitle = '';
  showPageHeader = false;
  compactPageHeaderSpacing = false;

  public isMobile = false;
  public isDrawerVisible = false;

  isMobile$: Observable<boolean> = this.breakpointObserver.observe(SHELL_MOBILE_BREAKPOINT).pipe(
    map((result: BreakpointState) => result.matches),
    shareReplay(),
  );

  version = AppVersion.currentVersion;

  resolvedNavItems: ResolvedShellNavItem[] = [];

  roleOptions: Array<{ label: string; value: PortalRole }> = [];
  activeRole: PortalRole | null = null;

  private destroy$ = new Subject<void>();
  private groupExpansionOverrides: Record<string, boolean> = {};
  private departmentDetailSubscription: Subscription | null = null;
  private currentDepartmentId: string | null = null;
  private currentDepartment: DepartmentDTO | null = null;
  private readonly pageHeaderHiddenRoutePrefixes = ['/admin/costs', '/admin/invite/'];
  private readonly compactHeaderSpacingRoutePrefixes = [
    '/admin/cost-api',
    '/admin/cost-control',
    '/in/dashboard',
    '/in/platform',
  ];

  private readonly dashboardRouteByRole: Record<PortalRole, string> = {
    instanceadmin: '/in/dashboard',
    admin: '/admin/dashboard',
    user: '/user/dashboard',
  };

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    public dataService: DataService,
    private configService: ConfigService,
    private roleContextService: RoleContextService,
    private readonly departmentCacheService: DepartmentCacheService,
  ) {
    this.tenantName = this.dataService.getCurrentTenantName();
    this.setCloudControlHeader();
  }

  ngOnInit(): void {
    this.updateRoleOptions();
    this.restoreSideNavState();
    this.syncDepartmentContext();
    this.updateResolvedNavItems();

    this.roleContextService.activeRole$.pipe(takeUntil(this.destroy$)).subscribe((role) => {
      this.activeRole = role;
      this.updateRoleOptions();
      this.restoreSideNavState();
      this.syncDepartmentContext();
      this.updateResolvedNavItems();
    });

    this.dataService.languageChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateRoleOptions();
      this.updateResolvedNavItems();
    });

    this.isMobile$.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      this.isMobile = isMobile;
      if (!isMobile) {
        this.isDrawerVisible = false;
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.closeDrawer();
        this.syncDepartmentContext();
        this.updateResolvedNavItems();
      });
  }

  ngOnDestroy(): void {
    this.departmentDetailSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  setCloudControlHeader(): void {
    const tenantName = this.dataService.getCurrentTenantName();
    if (tenantName) {
      this.portalHeader = tenantName;
    }
  }

  getDepartmentNamePlural(): string {
    return this.configService.getAdminPortalDepartmentNamePlural();
  }

  get departmentContextName(): string {
    return this.currentDepartment?.name ?? '';
  }

  toggleDrawer(): void {
    this.isDrawerVisible = !this.isDrawerVisible;
  }

  closeDrawer(): void {
    this.isDrawerVisible = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    this.closeDrawerOnOutsideInteraction(event.target);
  }

  @HostListener('document:touchstart', ['$event'])
  onDocumentTouchStart(event: TouchEvent): void {
    this.closeDrawerOnOutsideInteraction(event.target);
  }

  onSideNavGroupToggle(groupId: string): void {
    const currentExpanded = this.findResolvedItemById(this.resolvedNavItems, groupId)?.expanded ?? false;
    this.groupExpansionOverrides[groupId] = !currentExpanded;
    this.persistGroupExpansionState();
    this.updateResolvedNavItems();
  }

  onSideNavNavigate(): void {
    if (Object.keys(this.groupExpansionOverrides).length > 0) {
      this.groupExpansionOverrides = {};
      this.persistGroupExpansionState();
      this.updateResolvedNavItems();
    }
    this.closeDrawer();
  }

  onHomeClick(): void {
    const role = this.activeRole;
    this.router.navigateByUrl(role ? this.dashboardRouteByRole[role] : this.dashboardRouteByRole.user);
  }

  onRoleChange(role: PortalRole | null): void {
    if (!role) {
      return;
    }

    this.roleContextService.setActiveRole(role);
    this.router.navigateByUrl(this.dashboardRouteByRole[role]);
  }

  getActiveRoleLabel(): string {
    if (!this.activeRole) {
      return '';
    }

    return this.roleOptions.find((option) => option.value === this.activeRole)?.label ?? '';
  }

  getSideNavSectionLabel(): string {
    return this.dataService.getTexts().ShellNavSectionLabelNavigation;
  }

  getSideNavBrandTitle(): string {
    return this.dataService.getTexts().ShellNavBrandTitle;
  }

  private updateRoleOptions(): void {
    const availableRoles = this.roleContextService.getAvailableRoles();
    const labelByRole: Record<PortalRole, string> = {
      admin: 'Admin',
      user: 'User',
      instanceadmin: 'Instance Admin',
    };

    this.roleOptions = availableRoles.map((role) => ({
      value: role,
      label: labelByRole[role],
    }));

    if (!this.activeRole) {
      const preferredRole = this.roleContextService.chooseDefaultRole(availableRoles);
      if (preferredRole) {
        this.roleContextService.setActiveRole(preferredRole);
      }
    }
  }

  private closeDrawerOnOutsideInteraction(target: EventTarget | null): void {
    if (!this.isMobile || !this.isDrawerVisible || !(target instanceof Element)) {
      return;
    }

    const clickedInsideDrawer = !!target.closest('.p-drawer');
    const clickedOnMobileTrigger = !!target.closest('.mobile-nav-trigger');

    if (!clickedInsideDrawer && !clickedOnMobileTrigger) {
      this.closeDrawer();
    }
  }

  private updateResolvedNavItems(): void {
    const role = this.activeRole ?? 'user';
    const texts = this.dataService.getTexts();

    const context: ShellNavContext = {
      texts,
      departmentNamePlural: this.getDepartmentNamePlural(),
      activeRole: role,
      currentUrl: this.router.url,
    };

    const config = this.getNavConfigForRole(role, context);
    this.resolvedNavItems = this.resolveNavItems(config, context);
    this.updatePageHeaderState();
  }

  private updatePageHeaderState(): void {
    const currentPath = this.getCurrentPathname(this.router.url);
    this.compactPageHeaderSpacing = this.compactHeaderSpacingRoutePrefixes.some((prefix) =>
      currentPath.startsWith(prefix),
    );
    const shouldHideForRoute =
      currentPath === '/admin/dashboard' ||
      this.pageHeaderHiddenRoutePrefixes.some((prefix) => currentPath.startsWith(prefix));
    if (shouldHideForRoute) {
      this.pageHeaderTitle = '';
      this.showPageHeader = false;
      return;
    }

    const legacyRouteTitle = this.getLegacyPageHeaderTitle(currentPath);
    if (legacyRouteTitle) {
      this.pageHeaderTitle = legacyRouteTitle;
      this.showPageHeader = true;
      return;
    }

    const activeNavItem = this.findPrimaryActiveNavItem(this.resolvedNavItems);
    this.pageHeaderTitle = activeNavItem?.label?.trim() ?? '';
    this.showPageHeader = this.pageHeaderTitle.length > 0;
  }

  private getCurrentPathname(rawUrl: string): string {
    return new URL(rawUrl, 'http://localhost').pathname;
  }

  private findPrimaryActiveNavItem(items: ResolvedShellNavItem[]): ResolvedShellNavItem | null {
    for (const item of items) {
      if (item.items?.length) {
        const activeChild = this.findPrimaryActiveNavItem(item.items);
        if (activeChild) {
          return activeChild;
        }
      }

      if (item.active && item.routerLink?.length) {
        return item;
      }
    }

    return null;
  }

  private getLegacyPageHeaderTitle(pathname: string): string | null {
    if (pathname === '/admin/department') {
      const titleTemplate = this.dataService.getTexts().AdminDepartmentOverviewTitle;
      return this.formatWithDepartmentLabels(titleTemplate);
    }

    return null;
  }

  private formatWithDepartmentLabels(template: string): string {
    return template
      .replace('{{ departmentSingular }}', this.configService.getAdminPortalDepartmentNameSingular())
      .replace('{{ departmentPlural }}', this.configService.getAdminPortalDepartmentNamePlural());
  }

  private getNavConfigForRole(role: PortalRole, context: ShellNavContext): ShellNavItemConfig[] {
    const baseConfig = getShellNavConfigForRole(role);
    if (role !== 'admin') {
      return baseConfig;
    }

    const departmentId = this.getCurrentDepartmentIdFromUrl(context.currentUrl);
    if (!departmentId) {
      return baseConfig;
    }

    return baseConfig.map((item) => {
      if (item.id !== 'admin-department') {
        return item;
      }

      return {
        ...item,
        autoOpenOnRoutes: [...(item.autoOpenOnRoutes ?? []), '/admin/department/', '/admin/invite/'],
        items: this.buildDepartmentDetailItems(departmentId, this.currentDepartment?.invitationType === 1),
      };
    });
  }

  private getCurrentDepartmentIdFromUrl(url: string): string | null {
    const parsedUrl = new URL(url, 'http://localhost');
    const route = parsedUrl.pathname;
    const departmentDetailMatch = route.match(/^\/admin\/department\/([^/]+)$/);
    if (departmentDetailMatch && departmentDetailMatch[1] !== 'create') {
      return decodeURIComponent(departmentDetailMatch[1]);
    }

    if (route.startsWith('/admin/invite/')) {
      const inviteDepartmentId = parsedUrl.searchParams.get('departmentId');
      if (inviteDepartmentId) {
        return decodeURIComponent(inviteDepartmentId);
      }
    }

    return null;
  }

  private buildDepartmentDetailItems(departmentId: string, includeGroups: boolean): ShellNavItemConfig[] {
    if (this.currentDepartment === null) {
      return [];
    }

    const departmentRoute = ['/admin/department', departmentId];
    const isGroupSyncDepartment = includeGroups;
    const items: ShellNavItemConfig[] = [
      {
        id: 'admin-department-detail-cost-control',
        icon: 'pi pi-wallet',
        labelKey: 'DepartmentDetailTabCostControlLabel',
        routerLink: departmentRoute,
        queryParams: { tab: 'cost_control' },
        isDefaultForMissingQueryTab: true,
      },
      {
        id: 'admin-department-detail-users',
        icon: 'pi pi-user',
        labelKey: 'DepartmentDetailTabUsersLabel',
        routerLink: departmentRoute,
        queryParams: { tab: 'users' },
        autoOpenOnRoutes: ['/admin/invite/user'],
      },
      {
        id: 'admin-department-detail-logs',
        icon: 'pi pi-list',
        labelKey: 'DepartmentDetailTabLogsLabel',
        routerLink: departmentRoute,
        queryParams: { tab: 'logs' },
      },
      {
        id: 'admin-department-detail-settings',
        icon: 'pi pi-sliders-h',
        labelKey: 'DepartmentDetailTabSettingsLabel',
        routerLink: departmentRoute,
        queryParams: { tab: 'settings' },
      },
    ];

    if (isGroupSyncDepartment) {
      items.splice(2, 0, {
        id: 'admin-department-detail-groups',
        icon: 'pi pi-users',
        labelKey: 'DepartmentDetailTabGroupsLabel',
        routerLink: departmentRoute,
        queryParams: { tab: 'groups' },
        autoOpenOnRoutes: ['/admin/invite/group'],
      });
    }

    return items;
  }

  private syncDepartmentContext(): void {
    const departmentId = this.getCurrentDepartmentIdFromUrl(this.router.url);
    if (!departmentId) {
      this.currentDepartmentId = null;
      this.currentDepartment = null;
      this.departmentDetailSubscription?.unsubscribe();
      this.departmentDetailSubscription = null;
      return;
    }

    if (this.currentDepartmentId === departmentId && this.departmentDetailSubscription) {
      return;
    }

    this.currentDepartmentId = departmentId;
    this.currentDepartment = null;
    this.departmentDetailSubscription?.unsubscribe();
    this.departmentDetailSubscription = this.departmentCacheService
      .getDepartment$(departmentId, true)
      .subscribe((dep) => {
        this.currentDepartment = dep;
        this.updateResolvedNavItems();
      });
  }

  private resolveNavItems(configItems: ShellNavItemConfig[], context: ShellNavContext): ResolvedShellNavItem[] {
    const resolved: ResolvedShellNavItem[] = [];

    configItems.forEach((configItem) => {
      const resolvedItem = this.resolveNavItem(configItem, context);
      if (resolvedItem) {
        resolved.push(resolvedItem);
      }
    });

    return resolved;
  }

  private resolveNavItem(configItem: ShellNavItemConfig, context: ShellNavContext): ResolvedShellNavItem | null {
    const childItems = configItem.items ? this.resolveNavItems(configItem.items, context) : undefined;
    const hasChildren = !!childItems?.length;

    if (configItem.items && !hasChildren && !configItem.routerLink?.length) {
      return null;
    }

    const explicitActive = this.isRouteActive(configItem.routerLink, configItem.queryParams);
    const defaultActive = this.isDefaultQueryTabActive(configItem, context);
    const selfActive = explicitActive || defaultActive;
    const childActive = childItems?.some((item) => item.active) ?? false;
    const currentPath = this.getCurrentPathname(context.currentUrl);
    const routePrefixActive = (configItem.autoOpenOnRoutes ?? []).some(
      (prefix) => currentPath === prefix || currentPath.startsWith(`${prefix}/`),
    );
    const autoOpen = routePrefixActive;
    const expanded = hasChildren ? this.resolveGroupExpanded(configItem.id, autoOpen, childActive) : undefined;

    return {
      id: configItem.id,
      groupId: configItem.groupId,
      icon: configItem.icon,
      label: this.resolveLabel(configItem, context),
      routerLink: configItem.routerLink,
      queryParams: configItem.queryParams,
      items: childItems,
      active: selfActive || childActive || routePrefixActive,
      expanded,
      autoOpenOnRoutes: configItem.autoOpenOnRoutes,
    };
  }

  private resolveLabel(configItem: ShellNavItemConfig, context: ShellNavContext): string {
    if (configItem.labelFactory) {
      return configItem.labelFactory(context);
    }

    if (configItem.labelKey) {
      const label = context.texts[configItem.labelKey];
      return typeof label === 'string' ? label : '';
    }

    return '';
  }
  private isRouteActive(routerLink?: string[], queryParams?: Record<string, string>): boolean {
    if (!routerLink?.length) {
      return false;
    }

    return this.router.isActive(this.router.createUrlTree(routerLink, { queryParams }), {
      paths: 'exact',
      queryParams: 'exact',
      matrixParams: 'ignored',
      fragment: 'ignored',
    });
  }

  private isDefaultQueryTabActive(configItem: ShellNavItemConfig, context: ShellNavContext): boolean {
    if (!configItem.isDefaultForMissingQueryTab || !configItem.routerLink?.length) {
      return false;
    }

    const currentUrl = new URL(context.currentUrl, 'http://localhost');
    const tabParam = currentUrl.searchParams.get('tab');
    if (tabParam) {
      return false;
    }

    return this.isRouteActive(configItem.routerLink);
  }

  private resolveGroupExpanded(groupId: string, autoOpen: boolean, childActive: boolean): boolean {
    const override = this.groupExpansionOverrides[groupId];
    if (typeof override === 'boolean') {
      return override;
    }

    return autoOpen || childActive;
  }

  private findResolvedItemById(items: ResolvedShellNavItem[], id: string): ResolvedShellNavItem | null {
    for (const item of items) {
      if (item.id === id) {
        return item;
      }

      if (item.items?.length) {
        const nested = this.findResolvedItemById(item.items, id);
        if (nested) {
          return nested;
        }
      }
    }

    return null;
  }

  private restoreSideNavState(): void {
    this.restoreGroupExpansionState();
  }

  private restoreGroupExpansionState(): void {
    const key = this.getGroupStateStorageKey();
    if (!key) {
      this.groupExpansionOverrides = {};
      return;
    }

    const raw = sessionStorage.getItem(key);
    if (!raw) {
      this.groupExpansionOverrides = {};
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      this.groupExpansionOverrides = typeof parsed === 'object' && parsed ? parsed : {};
    } catch {
      this.groupExpansionOverrides = {};
    }
  }

  private persistGroupExpansionState(): void {
    const key = this.getGroupStateStorageKey();
    if (!key) {
      return;
    }

    sessionStorage.setItem(key, JSON.stringify(this.groupExpansionOverrides));
  }

  private getGroupStateStorageKey(): string | null {
    const scope = this.getStorageScope();
    return scope ? `ShellSideNavGroups:${scope}` : null;
  }

  private getStorageScope(): string | null {
    const tenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID);
    const role = this.activeRole ?? 'user';

    if (!tenantId) {
      return null;
    }

    return `${tenantId}:${role}`;
  }
}
